import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { Card, CardBody, CardHeader, Button, Spinner } from "@heroui/react";
import {
    LuArrowLeft,
    LuArrowRight,
    LuSave,
    LuPackage,
    LuImage,
    LuTag,
} from "react-icons/lu";
import { useNavigate, useParams } from "react-router-dom";
import { usePostData, useFetchData } from "@/hooks";
import { showToast, clearToast } from "@/utils";
import { ThemedText, PageHeader, Stepper, ErrorState } from "@/components/ui";
import {
    GeneralInfoStep,
    PricingStep,
    MediaStep,
} from "./components";

const STEPS = [
    { id: 1, title: "Informasi Umum", icon: LuPackage },
    { id: 2, title: "Harga & Satuan", icon: LuTag },
    { id: 3, title: "Media", icon: LuImage },
];

const ProductCreate = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const isEdit = !!id;

    const [currentStep, setCurrentStep] = useState(1);

    // Prices state (separate from form)
    const [prices, setPrices] = useState([]);

    // Image state as object
    const [imageState, setImageState] = useState({
        items: [],
        deleted: [],
    });

    const {
        control,
        register,
        handleSubmit,
        watch,
        trigger,
        reset,
        formState: { errors },
    } = useForm({
        defaultValues: {
            // Step 1
            name: "",
            material_category_id: "",
            material_sub_category_id: "",
            description: "",
        },
    });

    const categoryId = watch("material_category_id");

    // Fetch initial data for edit mode
    const fetchUrl = isEdit ? `materials/${id}/edit` : null;
    const {
        data: initialData,
        isLoading: isFetching,
        error: fetchError,
        refetch,
    } = useFetchData(fetchUrl);

    // Populate form when editing
    useEffect(() => {
        if (isEdit && initialData) {
            reset({
                name: initialData.name || "",
                material_category_id: initialData.material_category_id
                    ? String(initialData.material_category_id)
                    : "",
                material_sub_category_id: initialData.material_sub_category_id
                    ? String(initialData.material_sub_category_id)
                    : "",
                description: initialData.description || "",
            });

            // Set existing prices from server
            if (initialData.prices && initialData.prices.length > 0) {
                const existingPrices = initialData.prices.map((p) => ({
                    material_unit_id: String(p.material_unit_id),
                    price: Number(p.price) || 0,
                    conversion_factor: Number(p.conversion_factor) || 1,
                    is_default: Boolean(p.is_default),
                }));
                setPrices(existingPrices);
            }

            // Set existing images from server
            if (initialData.images && initialData.images.length > 0) {
                const existingImages = initialData.images.map((img) => ({
                    id: img.id,
                    preview: img.url_600,
                    isExisting: true,
                }));
                setImageState((prev) => ({ ...prev, items: existingImages }));
            }
        }
    }, [isEdit, initialData, reset]);

    // Fetch dropdown data
    const { data: categories, isLoading: isLoadingCategories } = useFetchData(
        "select/material-category",
    );
    const { data: subCategories, isLoading: isLoadingSubCategories } =
        useFetchData(
            categoryId
                ? `select/material-sub-category?category_id=${categoryId}`
                : null,
        );
    const { data: units, isLoading: isLoadingUnits } = useFetchData(
        "select/material-unit",
    );
    // Mutation
    const mutationEndpoint = isEdit
        ? `materials/${id}/update`
        : "materials/store";
    const productMutation = usePostData(mutationEndpoint);

    // Validation per step
    const validateStep = async (step) => {
        switch (step) {
            case 1:
                return await trigger(["name", "material_category_id"]);
            case 2:
                // Step 2 (Prices) - optional, but if any entry exists, it must be complete
                if (prices.length > 0) {
                    // Check all prices have required fields
                    const incompletePrices = prices.filter(
                        (p) =>
                            !p.material_unit_id ||
                            p.price === null ||
                            p.price === undefined,
                    );
                    if (incompletePrices.length > 0) {
                        showToast(
                            "warning",
                            "Perhatian",
                            "Lengkapi semua field satuan & harga, atau hapus entry yang tidak terpakai",
                        );
                        return false;
                    }
                }
                return true;
            case 3:
                // Step 3 (Media) is optional
                return true;
            default:
                return true;
        }
    };

    const handleNext = async () => {
        const isValid = await validateStep(currentStep);
        if (isValid) {
            clearToast();
            setCurrentStep((prev) => Math.min(prev + 1, STEPS.length));
        } else {
            showToast(
                "error",
                "Validasi Gagal",
                "Mohon lengkapi data yang wajib diisi.",
            );
        }
    };

    const handlePrev = () => {
        setCurrentStep((prev) => Math.max(prev - 1, 1));
    };

    const onSubmit = (data) => {
        // Build payload
        const payload = {
            name: data.name,
            material_category_id: data.material_category_id,
            active_status_id: 1,
            ...(data.material_sub_category_id && {
                material_sub_category_id: data.material_sub_category_id,
            }),
            ...(data.description && { description: data.description }),

            // Step 2 (Prices) - Only include if there are prices
            ...(prices.length > 0 && {
                prices: prices.map((p) => ({
                    material_unit_id: p.material_unit_id,
                    price: p.price || 0,
                    conversion_factor: p.conversion_factor || 1,
                    is_default: p.is_default || false,
                })),
            }),

        };

        // Use FormData only if NEW images exist (not existing server images)
        let submitData = payload;
        let headers = {};
        let method = isEdit ? "put" : "post";

        // Filter only new images (ones with file property, not isExisting)
        const newImages = imageState.items.filter(
            (img) => img.file && !img.isExisting,
        );
        const hasNewImages = newImages.length > 0;
        const hasDeletedImages = imageState.deleted.length > 0;

        // Need FormData if we have new images OR if we need to send deleted IDs
        if (hasNewImages || hasDeletedImages) {
            const formData = new FormData();

            // Append basic fields
            Object.entries(payload).forEach(([key, value]) => {
                if (typeof value === "object" && !Array.isArray(value)) {
                    Object.entries(value).forEach(
                        ([nestedKey, nestedValue]) => {
                            formData.append(`${key}[${nestedKey}]`, nestedValue);
                        },
                    );
                } else {
                    formData.append(key, value);
                }
            });

            // Append new images (file_type defined in backend)
            newImages.forEach((img, index) => {
                formData.append(`images[${index}]`, img.file);
            });

            // Append deleted image IDs
            imageState.deleted.forEach((id, index) => {
                formData.append(`deleted_images[${index}]`, id);
            });

            if (isEdit) {
                formData.append("_method", "PUT");
                method = "post";
            }
            submitData = formData;
            headers = { "Content-Type": "multipart/form-data" };
        }

        productMutation.mutate(submitData, {
            method,
            headers,
            onSuccess: () => {
                showToast(
                    "success",
                    "Berhasil",
                    `Produk berhasil ${isEdit ? "diperbarui" : "ditambahkan"}.`,
                );
                navigate("/products");
            },
            onError: (err) => {
                showToast(
                    "error",
                    "Gagal",
                    err.response?.data?.message || err.message,
                );
            },
        });
    };

    // Error state for edit mode
    if (isEdit && fetchError) {
        return (
            <ErrorState
                title="Gagal Memuat Data"
                description="Terjadi kesalahan saat memuat data produk. Silakan coba lagi."
                onAction={refetch}
            />
        );
    }

    return (
        <div className="mx-auto flex max-w-3xl flex-col gap-6">
            {/* Header */}
            <div className="flex gap-4">
                <Button isIconOnly variant="light" onPress={() => navigate(-1)}>
                    <LuArrowLeft size={20} />
                </Button>
                <PageHeader
                    title={isEdit ? "Edit Produk" : "Tambah Produk"}
                    breadcrumbs={[
                        { label: "Dashboard", href: "/" },
                        { label: "Produk", href: "/products" },
                        { label: isEdit ? "Edit" : "Tambah" },
                    ]}
                />
            </div>

            {/* Stepper */}
            <Card className="border-default-200 border" shadow="none">
                <CardBody className="p-6">
                    <Stepper steps={STEPS} currentStep={currentStep} />
                </CardBody>
            </Card>

            {/* Step Content */}
            <form onSubmit={(e) => e.preventDefault()} noValidate>
                <Card className="border-default-200 border" shadow="none">
                    <CardHeader className="bg-default-100/70 px-6 py-4">
                        <ThemedText className="font-semibold">
                            {STEPS[currentStep - 1].title}
                        </ThemedText>
                    </CardHeader>
                    <CardBody className="p-6">
                        {/* Loading state for edit mode */}
                        {isEdit && isFetching ? (
                            <div className="flex h-48 w-full items-center justify-center">
                                <Spinner
                                    label="Memuat data..."
                                    color="primary"
                                />
                            </div>
                        ) : (
                            <>
                                {/* Step 1: Informasi Umum */}
                                {currentStep === 1 && (
                                    <GeneralInfoStep
                                        control={control}
                                        register={register}
                                        errors={errors}
                                        categories={categories}
                                        subCategories={subCategories}
                                        isLoadingCategories={
                                            isLoadingCategories
                                        }
                                        isLoadingSubCategories={
                                            isLoadingSubCategories
                                        }
                                        categoryId={categoryId}
                                    />
                                )}

                                {/* Step 2: Harga & Satuan */}
                                {currentStep === 2 && (
                                    <PricingStep
                                        prices={prices}
                                        onChange={setPrices}
                                        units={units}
                                        isLoadingUnits={isLoadingUnits}
                                        productName={watch("name")}
                                    />
                                )}

                                {/* Step 3: Media */}
                                {currentStep === 3 && (
                                    <MediaStep
                                        imageState={imageState}
                                        setImageState={setImageState}
                                    />
                                )}
                            </>
                        )}
                    </CardBody>
                </Card>

                {/* Navigation Buttons */}
                <div className="mt-6 flex justify-between">
                    <Button
                        type="button"
                        variant="flat"
                        onPress={
                            currentStep === 1
                                ? () => navigate("/products")
                                : handlePrev
                        }
                        size="sm"
                        startContent={<LuArrowLeft size={18} />}
                    >
                        {currentStep === 1 ? "Batal" : "Sebelumnya"}
                    </Button>
                    <div className="flex gap-2">
                        {currentStep < STEPS.length ? (
                            <Button
                                type="button"
                                color="primary"
                                onPress={handleNext}
                                endContent={<LuArrowRight size={18} />}
                                size="sm"
                            >
                                Selanjutnya
                            </Button>
                        ) : (
                            <Button
                                color="primary"
                                type="button"
                                isLoading={productMutation.isLoading}
                                onPress={handleSubmit(onSubmit)}
                                size="sm"
                                startContent={
                                    !productMutation.isLoading && (
                                        <LuSave size={18} />
                                    )
                                }
                            >
                                {isEdit ? "Simpan Perubahan" : "Simpan Produk"}
                            </Button>
                        )}
                    </div>
                </div>
            </form>
        </div>
    );
};

export default ProductCreate;
